import type { FC, MutableRefObject } from 'react';
import { useEffect } from 'react';
import { Alert } from '@interstate/components/Alert';
import { Interstitial } from '@interstate/components/Interstitial';
import { useDispatch, useSelector } from 'react-redux';
import * as dealTemplateActionCreators from '@makemydeal/dr-dash-store/actions/dealTemplateActionCreators';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import { TemplateExistAlert } from './TemplateExists.Interstate';
import { useToastDisappear } from '../../../common/DataLoadingToast';
import { TEST_IDS } from '../constants';
import { Box } from '@interstate/components/Box';
import { TOAST_TIMEOUT } from '@interstate/components/Toast/Toast/store';
import type { DealTemplateInventoryTypeExtended, DraftDealTemplate } from '@makemydeal/dr-dash-types';
import TemplateListAccordion from './TemplateListAccordion';
import { useRecentlyRemovedTemplate } from './hooks/useRecentlyRemovedTemplate';
import { useRecentlyUpdatedTemplate } from './hooks/useRecentlyUpdatedTemplate';
import { TemplateAutoApplyConflictModal } from '../components/TemplateAutoApplyConflictModal';

type ManageTemplateWrapperProps = {
    wasTemplateRecentlySavedRef: MutableRefObject<boolean>;
    handleEditTemplate: (templateId: string) => void;
    isActive: boolean;
};

const ManageTemplatesWrapper: FC<ManageTemplateWrapperProps> = ({ wasTemplateRecentlySavedRef, handleEditTemplate, isActive }) => {
    const dispatch = useDispatch();
    const {
        name: savedTemplateName,
        overwritten: wasTemplateOverwritten,
        inventoryType: inventoryTypeForSavedTemplate
    } = useSelector(dealTemplateSelectors.getDraftDealTemplate) as DraftDealTemplate;

    const wasTemplateRecentlySaved = wasTemplateRecentlySavedRef.current;

    const shouldShowToastForSavedTemplate = wasTemplateRecentlySaved && savedTemplateName && !wasTemplateOverwritten;
    const {
        shouldShowSuccessToast: shouldShowToastForRemovedTemplate,
        shouldShowErrorToast: shouldShowToastForNotRemovedTemplate,
        templateName: removingTemplateName,
        inventoryType: inventoryTypeForRemovingTemplate,
        isRemoving: isTemplateRemoving,
        isRemoved: wasTemplateRecentlyRemoved
    } = useRecentlyRemovedTemplate();

    const {
        shouldShowSuccessToast: shouldShowToastForUpdatedTemplate,
        templateName: updatingTemplateName,
        shouldShowErrorToast: shouldShowToastForNotUpdatedTemplate,
        id: updatingTemplateId,
        inventoryType: inventoryTypeForUpdatingTemplate,
        isUpdating: isTemplateUpdating,
        isUpdated: wasTemplateRecentlyUpdated,
        isUpdatingError: wasTemplateUpdateError,
        hasTemplateConflictError: hasConflictErrorDuringTemplateUpdate,
        templatesWithConflict,
        setHasTemplateConflictError: setHasConflictErrorDuringTemplateUpdate
    } = useRecentlyUpdatedTemplate();

    const { toastShown: shouldShowToastForOverwrittenTemplate, triggerToast: showTemplateOverwrittenToast } =
        useToastDisappear(TOAST_TIMEOUT);

    const hasTemplateOverwrittenRecently = wasTemplateRecentlySaved && savedTemplateName && wasTemplateOverwritten;
    useEffect(() => {
        if (hasTemplateOverwrittenRecently) {
            showTemplateOverwrittenToast();
        }
    }, [hasTemplateOverwrittenRecently]);

    useEffect(() => {
        if (isActive) {
            dispatch(dealTemplateActionCreators.getDealTemplates());
        }
    }, [isActive, dispatch]);

    useEffect(() => {
        // Always refetch after save or remove events.
        if (wasTemplateRecentlySaved || wasTemplateRecentlyRemoved) {
            dispatch(dealTemplateActionCreators.getDealTemplates());
            return;
        }
    }, [wasTemplateRecentlySaved, wasTemplateRecentlyRemoved]);

    useEffect(() => {
        if (wasTemplateRecentlyUpdated && !wasTemplateUpdateError) {
            dispatch(dealTemplateActionCreators.getDealTemplates());
            return;
        }
    }, [wasTemplateRecentlyUpdated, wasTemplateUpdateError]);

    const getInventoryTypeForRecentlyAffectedTemplate = (): DealTemplateInventoryTypeExtended | undefined => {
        if (wasTemplateRecentlySaved) {
            return (inventoryTypeForSavedTemplate || 'All') as DealTemplateInventoryTypeExtended;
        }

        if (wasTemplateRecentlyUpdated) {
            return inventoryTypeForUpdatingTemplate || 'All';
        }

        if (wasTemplateRecentlyRemoved) {
            return inventoryTypeForRemovingTemplate;
        }

        return undefined;
    };

    if (!isActive) {
        return null;
    }

    return (
        <>
            {(isTemplateRemoving || isTemplateUpdating) && <Interstitial fullScreen size="small" />}
            <TemplateAutoApplyConflictModal
                show={hasConflictErrorDuringTemplateUpdate}
                templatesWithConflict={templatesWithConflict}
                onCancel={() => {
                    setHasConflictErrorDuringTemplateUpdate(false);
                }}
                onConfirm={() => {
                    dispatch(
                        dealTemplateActionCreators.updateDealTemplate(
                            { id: updatingTemplateId, isAutoApply: true, forceAutoApply: true },
                            {
                                name: updatingTemplateName,
                                id: updatingTemplateId,
                                inventoryType: inventoryTypeForUpdatingTemplate
                            }
                        )
                    );
                    setHasConflictErrorDuringTemplateUpdate(false);
                }}
            />

            <Box data-testid={TEST_IDS.MANAGE_EXISTING_TEMPLATES_WRAPPER}>
                {shouldShowToastForSavedTemplate && (
                    <Box mb={2}>
                        <Alert
                            type="success"
                            data-testid={TEST_IDS.TEMPLATE_SAVE_SUCCESS_TOAST}
                            title="Template Successfully Saved"
                        >
                            We have successfully saved "<strong>{savedTemplateName}</strong>" template.
                        </Alert>
                    </Box>
                )}
                {shouldShowToastForOverwrittenTemplate && (
                    <Box mb={2}>
                        <TemplateExistAlert templateName={savedTemplateName as string} />
                    </Box>
                )}
                {shouldShowToastForRemovedTemplate && (
                    <Box mb={2}>
                        <Alert type="success" data-testid={TEST_IDS.DELETE_TEMPLATE_SUCCESS_TOAST} title="Successfully removed">
                            We have successfully removed "<strong>{removingTemplateName}</strong>" template.
                        </Alert>
                    </Box>
                )}
                {shouldShowToastForNotRemovedTemplate && (
                    <Box mb={2}>
                        <Alert type="error" data-testid={TEST_IDS.DELETE_TEMPLATE_ERROR_TOAST} title="Failure to remove">
                            The "<strong>{removingTemplateName}</strong>" template could not be deleted.
                        </Alert>
                    </Box>
                )}
                {shouldShowToastForNotUpdatedTemplate && (
                    <Box mb={2}>
                        <Alert type="error" data-testid={TEST_IDS.UPDATE_TEMPLATE_ERROR_TOAST} title="Failure to update">
                            The "<strong>{updatingTemplateName}</strong>" template could not be updated.
                        </Alert>
                    </Box>
                )}
                {shouldShowToastForUpdatedTemplate && (
                    <Box mb={2}>
                        <Alert type="success" data-testid={TEST_IDS.UPDATE_TEMPLATE_SUCCESS_TOAST} title="Successfully updated">
                            We have successfully updated "<strong>{updatingTemplateName}</strong>" template.
                        </Alert>
                    </Box>
                )}
                <TemplateListAccordion
                    handleEditTemplate={handleEditTemplate}
                    expandedItem={getInventoryTypeForRecentlyAffectedTemplate()}
                />
            </Box>
        </>
    );
};

export default ManageTemplatesWrapper;
